import {AutogenLayoutSurveyControl} from "./autogen/AutogenLayoutSurveyControl";
import {
    BlurEvent,
    ChangeEvent,
    ClickEvent,
    Cursor,
    DataDisplayEvent,
    DataSourceAction,
    DataSourceExecutionEvent,
    DataSourceMode,
    Panel,
    PrintableEvent,
    Snackbar,
    TableRowDisplayEvent,
    TableSelectionEvent,
    Textbox, ValidationResult,
} from "@mcleod/components";
import {Api, EmailUtil, StringUtil} from "@mcleod/core";
import {ImageName} from "@mcleod/images";
import {formatSqlQuery, testSqlQuery} from "./utils/SqlTestUtils";

export class SurveyControl extends AutogenLayoutSurveyControl {

    onLoad(): void | Promise<void> {
        this.buttonTestQuery.disabledTooltip = "Select a row below to test query results";
        this.tableSurveyDetail.onEditLayoutLoaded = (decorator, tableRow) => {
            decorator.multiButton.dropdownItems = null;
        }

        this.textboxEmail.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.emailDomainValidator(this.textboxEmail, mode);
        };
    }

    async sourceTia3plSurveyControlAfterExecution(event: DataSourceExecutionEvent) {
        if(event.getAction() === DataSourceAction.SEARCH){
            await this.sourceTia3plSurveyDetail.search();
        }
    }


    tableSurveyDetailOnRowDisplay(event: TableRowDisplayEvent){
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const textboxSqlQuery = tableRow.findComponentById("textboxSqlQuery") as Textbox;
        textboxSqlQuery.text = formatSqlQuery(modelRow.getString("sql_query",""));
        modelRow.set("sql_query", formatSqlQuery(modelRow.getString("sql_query","")));

    }


    tableSurveyDetailOnSelect(event: TableSelectionEvent) {
        const tableRow = event.newRow;
        const selectedRows = tableRow?.table?.selectedRows;
        if (!selectedRows || selectedRows.length === 0) {
            this.setupTestQueryButtonAndLabel(false);
            return;
        }
        this.setupTestQueryButtonAndLabel(true);
        return;

    }

    textboxTokenOnChange(event: ChangeEvent) {
        if(event.userInitiatedChange){
            this.labelVerificationStatus.visible = false;
        }
    }

    tableSurveyDetailOnBlur(event: BlurEvent) {
        this.setupTestQueryButtonAndLabel(false);
    }

    textboxTokenOnBlur(event: BlurEvent) {
        this.setupVerifyButtonAndLabel();
    }

    textboxSurveyUrlOnBlur(event: BlurEvent) {
        this.setupVerifyButtonAndLabel();
    }

    textboxStartDateOnBlur(event: BlurEvent) {
        this.validateDates('start');
    }

    textboxEndDateOnBlur(event: BlurEvent) {
        this.validateDates('end');
    }

    textboxTokenOnDataDisplay(event: DataDisplayEvent) {
        this.setupVerifyButtonAndLabel();
    }

    textboxSurveyUrlOnDataDisplay(event: DataDisplayEvent) {
        this.setupVerifyButtonAndLabel();
    }


    async buttonVerifyConnectionOnClick(event: ClickEvent) {
        const token = this.textboxToken.text;
        const url = this.textboxSurveyUrl.text;
        this.labelVerificationStatus.tooltipDynamicContent = null;
        this.labelVerificationStatus.tooltip = null;
        try {
            const response = await Api.post("lme/powerbroker/tia-validations", {
                is_verification: true,
                token: token,
                url: url
            });
            this.labelVerificationStatus.visible = true;

            if (response && response.data && response.data.length > 0) {
                const responseData = response.data[0];

                if (responseData.error) {
                    // Error from API
                    this.labelVerificationStatus.imageName = ImageName.circleX;
                    this.labelVerificationStatus.imageColor = "error";
                    this.labelVerificationStatus.tooltip = responseData.error;
                    Snackbar.showWarningSnackbar(`Verification failed: ${responseData.error}`);
                } else if (responseData.response && responseData.response.includes("Valid credentials")) {
                    // Successful verification
                    this.labelVerificationStatus.imageName = ImageName.circleCheck;
                    this.labelVerificationStatus.imageColor = "success";
                    this.labelVerificationStatus.tooltipDynamicContent = this.createTooltipPanel(responseData.expiration_date);
                } else {
                    // Invalid or unexpected response
                    this.labelVerificationStatus.imageName = ImageName.circleX;
                    this.labelVerificationStatus.imageColor = "error";
                    this.labelVerificationStatus.tooltip = "Invalid verification";
                }
            } else {
                // Empty or missing response
                this.labelVerificationStatus.imageName = ImageName.circleX;
                this.labelVerificationStatus.imageColor = "error";
                this.labelVerificationStatus.tooltip = "No response from server";
            }
        } catch (error) {
            // Exception during API call
            this.labelVerificationStatus.imageName = ImageName.circleX;
            this.labelVerificationStatus.imageColor = "error";
            this.labelVerificationStatus.tooltip = `Error: ${error?.message || "Unknown error"}`;
            Snackbar.showWarningSnackbar(`API error: ${error?.message || "Unknown error"}`);
        }
    }

    async buttonLastRunOnClick(event: ClickEvent) {
        try {
            const response = await Api.post("lme/powerbroker/tia-validations", {is_last_run: true});
            this.textboxLastRunDate.visible = true;

            // Check if we have a valid response with data
            if (response && response.data && response.data.length > 0) {
                const responseData = response.data[0];

                if (responseData.error) {
                    // Show error if returned from API
                    Snackbar.showWarningSnackbar(`Last Run Failed: ${responseData.error}`);
                    this.textboxLastRunDate.text = "";
                } else if (responseData.response) {
                    // Show the date returned from the API
                    this.textboxLastRunDate.text = responseData.response;
                } else {
                    // No response data or unexpected format
                    Snackbar.showWarningSnackbar("No last run date available");
                    this.textboxLastRunDate.text = "";
                }
            } else {
                Snackbar.showWarningSnackbar("Failed to retrieve last run date");
                this.textboxLastRunDate.text = "";
            }
        } catch (error) {
            Snackbar.showWarningSnackbar(`Error retrieving last run date: ${error.message || "Unknown error"}`);
            this.textboxLastRunDate.text = "";
        }
    }

    async buttonTestQueryOnClick(event: ClickEvent) {
        if (this.tableSurveyDetail.selectedRows.length === 0) {
            Snackbar.showWarningSnackbar("Please select a survey report to test.");
        } else {
            const modelRow = this.tableSurveyDetail.selectedRows[0]?.data;
            if (modelRow) {
                const id = modelRow.getString("id", null);
                const query = modelRow.getString("sql_query", null);
                if (StringUtil.isEmptyString(query)) {
                    Snackbar.showWarningSnackbar("Please provide a SQL Query to test.");
                    return;
                }
                if(StringUtil.isEmptyString(id)) {
                    Snackbar.showWarningSnackbar("Selected survey report does not have a valid ID.");
                    return;
                }
                await testSqlQuery(this, id, query);
            }
        }
    }

    textboxSqlQueryOnPrintable(event: PrintableEvent) {
        event.target.maxHeight = event.printable ? 88 : 125;
    }



    private setupVerifyButtonAndLabel() {
        if (!StringUtil.isEmptyString(this.textboxToken.text) && !StringUtil.isEmptyString(this.textboxSurveyUrl.text)) {
            this.buttonVerifyConnection.visible = true;
            this.buttonLastRun.visible = true;
        } else {
            this.buttonLastRun.visible = false;
            this.buttonVerifyConnection.visible = false;
            this.labelVerificationStatus.visible = false;
        }
    }

    private validateDates(onBlurField: 'start' | 'end') {
        if (!StringUtil.isEmptyString(this.textboxStartDate.text) && !StringUtil.isEmptyString(this.textboxEndDate.text)) {
            const startDate = new Date(this.textboxStartDate.text);
            const endDate = new Date(this.textboxEndDate.text);
            if (endDate < startDate) {
                if (StringUtil.equalsIgnoreCase("start", onBlurField)) {
                    this.textboxStartDate.text = "";
                    Snackbar.showWarningSnackbar("Start Date must be before the End Date.");
                } else {
                    this.textboxEndDate.text = "";
                    Snackbar.showWarningSnackbar("End Date must be after Start Date.");
                }
            }
        }
    }

    private setupTestQueryButtonAndLabel(value: boolean) {
        this.buttonTestQuery.enabled = value;
        this.buttonTestQuery.visible = true;
        if (this.buttonTestQuery.enabled) {
            this.buttonTestQuery.disabledTooltip = null;
                this.buttonTestQuery.tooltip = "Run query count";
        } else {
            this.buttonTestQuery.tooltip = null;
            this.buttonTestQuery.disabledTooltip = "Select a row below to test query results";
        }
    }


    private createTooltipPanel(expiration_date: any) {
        const panel = new Panel({ id: "panelHoverToDisplay", maxWidth: 400, cursor: Cursor.DEFAULT }) as Panel;
        const labelExpirationMessage = new Textbox({ id: "expiresOn", text: expiration_date, printable: true, caption: "Expires on:", rowBreak: true, enabled: false });
        panel.add(labelExpirationMessage);
        return panel;
    }

    private emailDomainValidator(textbox: Textbox, mode: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if(StringUtil.isEmptyString(textbox.text)){
            // Allow empty value
           return { isValid: true, component: textbox, validationMessage: null };
        }

        const result: ValidationResult = { isValid: true, component: textbox, validationMessage: null };
        const domains = textbox.text.split(",");
        for (const domain of domains) {
            if (!EmailUtil.isValidEmail(domain.trim())) {
                return { component: textbox, isValid: false, validationMessage: "Invalid domain format. Email domains must be in the following format [abc.com,xyz.com]" }
            }
        }
        return null;
    }
}
